import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  IconButton,
  Paper,
  styled,
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  CheckCircleOutline as CheckCircleIcon,
  Visibility,
  VisibilityOff,
  MarkEmailRead as MarkEmailReadIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { isValidCPF, isValidCNPJ } from "@brazilian-utils/brazilian-utils";

import MainHeader from "../../components/MainHeader";
import HeaderSecti from "../../components/HeaderSecti";
import Rodape from "../../components/Rodape";
import ResponsiveModal from "../../components/ResponsiveModal";

import api from "../../services/api";
import history from "../../services/history";

// ---------------------------------------------------------------------------
// /alterar-email (e /recuperar-acesso) no mesmo visual da /recuperar-senha
// (redesenho de 08/10/2026): painel explicativo à esquerda, cartão branco com
// o formulário à direita, fundo creme, Poppins. Os campos, as validações, as
// máscaras, o bloqueio de colar na confirmação e as rotas da API são os mesmos
// da versão anterior — só a apresentação mudou.
//
// Cabeçalho público (HeaderSecti + MainHeader) só para quem NÃO está logado:
// logado, o layout já põe o cabeçalho da plataforma e os dois empilhavam.
//
// ORDEM DO ARQUIVO: FONTE e COR são lidos pelos styled() abaixo em tempo de
// avaliação do módulo — declarar depois deles dá ReferenceError (tela branca).
// ---------------------------------------------------------------------------
const FONTE = "'Poppins', system-ui, sans-serif";

const COR = {
  fundo: "#FFF9EF",
  cartao: "#FFFFFF",
  linha: "#EDE4D6",
  tinta: "#1F1E1C",
  textoSuave: "#6B655D",
  apagado: "#8C8478",
  rotulo: "#3B3833",
  campoBorda: "#E2DACD",
  campoFundo: "#FDFBF7",
  laranja: "#D2551D",
  laranjaHover: "#B8460F",
  laranjaClaro: "#FBE7DB",
  azul: "#2C5FA8",
  azulHover: "#24518F",
  azulClaro: "#E3ECF9",
  creme: "#FDF6E7",
  pessego: "#FBEADF",
};

const CampoTexto = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    minHeight: 54,
    borderRadius: 11,
    backgroundColor: COR.campoFundo,
    fontFamily: FONTE,
    fontSize: 15.5,
    color: COR.tinta,
    transition: "background-color .15s, box-shadow .15s",
    "& fieldset": { borderWidth: 1.5, borderColor: COR.campoBorda },
    "&:hover fieldset": { borderColor: COR.campoBorda },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 4px rgba(44, 95, 168, .13)",
    },
    "&.Mui-focused fieldset": { borderWidth: 1.5, borderColor: COR.azul },
    "&.Mui-error fieldset": { borderColor: COR.laranja },
    "& legend": { display: "none" },
  },
  "& .MuiInputBase-input": {
    height: 54,
    boxSizing: "border-box",
    padding: "0 8px",
    fontFamily: FONTE,
    "&::placeholder": { color: "#A79E92", opacity: 1 },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: FONTE,
    fontSize: 12.5,
    marginLeft: 2,
    marginTop: 6,
    "&.Mui-error": { color: COR.laranja },
  },
});

const PASSOS = [
  "Informe o novo e-mail duas vezes",
  "Confirme seu documento e a senha atual",
  "Entre na plataforma usando o novo e-mail",
];

const ABAS = [
  {
    valor: "0",
    tipo: "PF",
    titulo: "Pessoa física",
    rota: "aluno/recover-access",
    cor: COR.laranja,
    corHover: COR.laranjaHover,
    corSuave: COR.laranjaClaro,
  },
  {
    valor: "1",
    tipo: "PJ",
    titulo: "Pessoa jurídica",
    rota: "empresa/recover-access",
    cor: COR.azul,
    corHover: COR.azulHover,
    corSuave: COR.azulClaro,
  },
];

const unmask = (raw) => String(raw || "").replace(/[^0-9]/g, "");

const formatCPF = (v) =>
  unmask(v)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const formatCNPJ = (v) =>
  unmask(v)
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");

// Confirmação do e-mail tem de ser digitada (sem colar), como antes.
const blockPaste = (e) => {
  if (e.ctrlKey && (e.key === "v" || e.keyCode === 86)) {
    e.preventDefault();
  }
};

function Circulo({ cor, tamanho, ...pos }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        width: tamanho,
        height: tamanho,
        borderRadius: "50%",
        bgcolor: cor,
        pointerEvents: "none",
        ...pos,
      }}
    />
  );
}

function Rotulo({ children, htmlFor }) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      sx={{
        display: "block",
        fontFamily: FONTE,
        fontSize: 13.5,
        fontWeight: 600,
        color: COR.rotulo,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

const icone = (Icon) => (
  <InputAdornment position="start">
    <Icon sx={{ color: COR.apagado, fontSize: 20 }} />
  </InputAdornment>
);

function RecoveryForm({ aba, onSubmit, loading }) {
  const isPF = aba.tipo === "PF";
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [documento, setDocumento] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!email?.trim()) errs.email = "Campo obrigatório.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = "E-mail inválido.";

    if (!emailConfirm?.trim()) errs.emailConfirm = "Campo obrigatório.";
    else if (email !== emailConfirm)
      errs.emailConfirm = "Os e-mails não coincidem.";

    if (!documento?.trim()) errs.documento = "Campo obrigatório.";
    else if (isPF && !isValidCPF(documento)) errs.documento = "CPF inválido.";
    else if (!isPF && !isValidCNPJ(documento)) errs.documento = "CNPJ inválido.";

    if (!password?.trim()) errs.password = "Campo obrigatório.";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      email: email.trim(),
      password,
      [isPF ? "cpf" : "cnpj"]: unmask(documento),
    });
  };

  const clearError = (field) =>
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));

  const pref = `alt-${aba.tipo}`;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        <Box>
          <Rotulo htmlFor={`${pref}-email`}>Novo e-mail</Rotulo>
          <CampoTexto
            id={`${pref}-email`}
            fullWidth
            type="email"
            placeholder="novo@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError("email");
            }}
            error={!!errors.email}
            helperText={errors.email}
            InputProps={{ startAdornment: icone(EmailIcon) }}
          />
        </Box>

        <Box>
          <Rotulo htmlFor={`${pref}-confirma`}>Confirme o novo e-mail</Rotulo>
          <CampoTexto
            id={`${pref}-confirma`}
            fullWidth
            type="email"
            placeholder="Digite o e-mail de novo"
            value={emailConfirm}
            onChange={(e) => {
              setEmailConfirm(e.target.value);
              clearError("emailConfirm");
            }}
            onPaste={(e) => e.preventDefault()}
            onKeyDown={blockPaste}
            error={!!errors.emailConfirm}
            helperText={errors.emailConfirm}
            InputProps={{ startAdornment: icone(CheckCircleIcon) }}
          />
        </Box>

        <Box>
          <Rotulo htmlFor={`${pref}-doc`}>{isPF ? "CPF" : "CNPJ da empresa"}</Rotulo>
          <CampoTexto
            id={`${pref}-doc`}
            fullWidth
            placeholder={isPF ? "000.000.000-00" : "00.000.000/0000-00"}
            value={documento}
            onChange={(e) => {
              setDocumento(isPF ? formatCPF(e.target.value) : formatCNPJ(e.target.value));
              clearError("documento");
            }}
            inputProps={{ inputMode: "numeric", maxLength: isPF ? 14 : 18 }}
            error={!!errors.documento}
            helperText={errors.documento}
            InputProps={{ startAdornment: icone(isPF ? BadgeIcon : BusinessIcon) }}
          />
        </Box>

        <Box>
          <Rotulo htmlFor={`${pref}-senha`}>Senha atual</Rotulo>
          <CampoTexto
            id={`${pref}-senha`}
            fullWidth
            placeholder="Sua senha cadastrada"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearError("password");
            }}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              startAdornment: icone(LockIcon),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                    sx={{ color: COR.apagado, mr: 0.25 }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Stack>

      <Button
        type="submit"
        fullWidth
        disabled={loading}
        startIcon={!loading && <MarkEmailReadIcon />}
        sx={{
          mt: 3,
          py: 1.6,
          borderRadius: 2.5,
          fontFamily: FONTE,
          fontSize: 16,
          fontWeight: 700,
          textTransform: "none",
          color: "#fff",
          bgcolor: aba.cor,
          boxShadow: "none",
          "&:hover": { bgcolor: aba.corHover, boxShadow: "none" },
          "&.Mui-disabled": { bgcolor: aba.corSuave, color: "#fff" },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Confirmar alteração"}
      </Button>
    </form>
  );
}

export default function EmailRecovery() {
  const signed = useSelector((s) => s?.auth?.signed);
  const [tabValue, setTabValue] = useState("0");
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const aba = ABAS.find((a) => a.valor === tabValue) || ABAS[0];

  const enviar = async (data) => {
    setLoading(true);
    try {
      await api.put(aba.rota, data);
      setSuccessOpen(true);
    } catch (err) {
      const msg = err?.response?.data;
      enqueueSnackbar(
        (typeof msg === "string" && msg) || msg?.error || "Erro ao alterar e-mail.",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    history.push("/login");
  };

  return (
    <>
      {!signed && (
        <>
          <HeaderSecti />
          <MainHeader page="login" />
        </>
      )}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: COR.fundo,
          minHeight: { xs: "auto", md: "76vh" },
          width: "100%",
          py: { xs: 4, md: 8 },
          px: { xs: 2, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONTE,
        }}
      >
        <Circulo cor={COR.creme} tamanho={340} top={-90} left={-120} />
        <Circulo cor={COR.pessego} tamanho={420} bottom={-190} right={-140} />

        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 1180,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 8 },
            alignItems: "center",
          }}
        >
          {/* Painel explicativo */}
          <Box>
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: COR.azul,
                mb: 1,
              }}
            >
              Dados de acesso
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: FONTE,
                fontSize: { xs: 34, md: 46 },
                fontWeight: 700,
                color: COR.laranja,
                lineHeight: 1.1,
                mb: 2.5,
              }}
            >
              Alterar e-mail
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 17,
                color: COR.rotulo,
                lineHeight: 1.6,
                maxWidth: 460,
                mb: 3.5,
              }}
            >
              Atualize o e-mail cadastrado na plataforma <strong>OxeTech</strong>.
              Para proteger sua conta, pedimos seu documento e a senha atual.
            </Typography>

            <Stack spacing={2} sx={{ mb: 3.5 }}>
              {PASSOS.map((passo, i) => (
                <Stack key={passo} direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: COR.laranjaClaro,
                      color: COR.laranjaHover,
                      fontFamily: FONTE,
                      fontSize: 14,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontFamily: FONTE, fontSize: 15.5, color: COR.rotulo }}>
                    {passo}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Typography sx={{ fontFamily: FONTE, fontSize: 15.5, color: COR.textoSuave }}>
              Esqueceu a senha?{" "}
              <Link
                to="/recuperar-senha"
                style={{ color: COR.laranja, fontWeight: 700, textDecoration: "none" }}
              >
                Recuperar senha
              </Link>
            </Typography>
          </Box>

          {/* Cartão do formulário */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              border: `1px solid ${COR.linha}`,
              bgcolor: COR.cartao,
              boxShadow: "0 18px 48px rgba(31,30,28,.07)",
              maxWidth: 520,
              width: "100%",
              mx: "auto",
            }}
          >
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 27,
                fontWeight: 700,
                color: COR.tinta,
                textAlign: "center",
              }}
            >
              Alterar e-mail
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 15,
                color: COR.textoSuave,
                textAlign: "center",
                mt: 0.75,
                mb: 3,
              }}
            >
              Escolha o tipo de conta e preencha os dados
            </Typography>

            {/* Seletor em pílula */}
            <Box
              role="tablist"
              aria-label="Tipo de conta"
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.5,
                p: 0.5,
                mb: 3,
                borderRadius: 2.5,
                bgcolor: COR.creme,
                border: `1px solid ${COR.linha}`,
              }}
            >
              {ABAS.map((a) => {
                const ativa = a.valor === tabValue;
                return (
                  <Button
                    key={a.valor}
                    role="tab"
                    aria-selected={ativa}
                    onClick={() => setTabValue(a.valor)}
                    disableRipple
                    sx={{
                      py: 1.15,
                      borderRadius: 2,
                      fontFamily: FONTE,
                      fontSize: 15,
                      fontWeight: ativa ? 700 : 600,
                      textTransform: "none",
                      color: ativa ? a.cor : COR.textoSuave,
                      bgcolor: ativa ? "#fff" : "transparent",
                      boxShadow: ativa ? "0 1px 4px rgba(31,30,28,.10)" : "none",
                      "&:hover": { bgcolor: ativa ? "#fff" : "rgba(255,255,255,.55)" },
                    }}
                  >
                    {a.titulo}
                  </Button>
                );
              })}
            </Box>

            {/* key: troca de aba zera o formulário, como as abas antigas faziam */}
            <RecoveryForm key={aba.valor} aba={aba} onSubmit={enviar} loading={loading} />

            {/* Divisor "ou" */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2.5 }}>
              <Box sx={{ flex: 1, height: 1, bgcolor: COR.linha }} />
              <Typography sx={{ fontFamily: FONTE, fontSize: 13.5, color: COR.apagado }}>ou</Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: COR.linha }} />
            </Stack>

            <Button
              component={Link}
              to={signed ? "/dashboard" : "/login"}
              fullWidth
              startIcon={<ArrowBackIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                fontFamily: FONTE,
                fontSize: 15.5,
                fontWeight: 700,
                textTransform: "none",
                color: COR.azul,
                border: `1.5px solid ${COR.azul}`,
                "&:hover": { bgcolor: COR.azulClaro },
              }}
            >
              {signed ? "Voltar ao meu painel" : "Voltar para o login"}
            </Button>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              justifyContent="center"
              sx={{ mt: 2.5 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: COR.apagado, mt: "2px" }} />
              <Typography
                sx={{ fontFamily: FONTE, fontSize: 13, color: COR.apagado, textAlign: "center", lineHeight: 1.5 }}
              >
                Não lembra a senha nem tem acesso ao e-mail?{" "}
                <Link to="/ajuda" style={{ color: COR.azul, fontWeight: 600, textDecoration: "none" }}>
                  Preciso de ajuda
                </Link>
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Box>

      <Rodape />

      <ResponsiveModal
        open={successOpen}
        onClose={handleSuccessClose}
        title="E-mail alterado"
        actions={
          <Button onClick={handleSuccessClose} autoFocus sx={{ fontFamily: FONTE }}>
            OK
          </Button>
        }
      >
        <Typography sx={{ fontFamily: FONTE }}>
          Seu e-mail foi alterado com sucesso. Use o novo e-mail para fazer login.
        </Typography>
      </ResponsiveModal>
    </>
  );
}
